import { useState, useCallback, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import apiService from '../services/api.service';
import { getSpecificErrorMessage } from '../utils/errorHelpers';

const useBarcodeScanner = (onScanComplete) => {
  const [isScanning, setIsScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cachedLookups, setCachedLookups] = useState({});
  const MAX_CACHE_SIZE = 50; // Limit cache size to prevent memory issues
  const { enqueueSnackbar } = useSnackbar();

  // Clear error after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const lookupBarcode = useCallback(async (barcode) => {
    // Validate barcode format - allow more characters for various barcode types
    // Including spaces for formatted ISBNs and other common barcode characters
    if (!barcode || typeof barcode !== 'string' || !barcode.match(/^[A-Za-z0-9\s_\-\.]+$/)) {
      console.error('Invalid barcode format:', { barcode, type: typeof barcode });
      setError('Invalid barcode format');
      enqueueSnackbar('Invalid barcode format. Please check the scanned code.', { variant: 'error' });
      return null;
    }
    
    // Clean up the barcode - trim whitespace and normalize
    const cleanedBarcode = barcode.trim();

    // Check cache first
    if (cachedLookups[barcode]) {
      const cached = cachedLookups[barcode];
      if (Date.now() - cached.timestamp < 5 * 60 * 1000) { // 5 minute cache
        return cached.data;
      }
    }

    setIsLoading(true);
    setError(null);

    try {
      const url = `barcode/lookup?barcode=${encodeURIComponent(cleanedBarcode || barcode)}`;
      const response = await apiService.get(url);
      
      // Handle the nested response structure from itemResponse wrapper
      let responseData;
      if (response.data.success && response.data.data) {
        // New structure: { success: true, data: { status: 'success', data: {...} } }
        if (response.data.data.status === 'success') {
          responseData = response.data.data.data;
        } else {
          responseData = response.data.data;
        }
      } else if (response.data.status === 'success') {
        // Old structure: { status: 'success', data: {...} }
        responseData = response.data.data;
      }
      
      if (responseData) {
        
        // Handle not found case
        if (!responseData.found) {
          enqueueSnackbar('No equipment found for this barcode. Create a new item.', { variant: 'info' });
          return null; // Return null to trigger fallback form population
        }
        
        // Extract prefill data
        const data = responseData.prefillData || responseData;
        
        // Handle fuzzy match warning (but not for templates)
        if (response.data.warning && responseData.source !== 'equipment_template') {
          enqueueSnackbar(response.data.warning, { variant: 'warning', autoHideDuration: 3000 });
        }
        
        // Cache the result with match info
        setCachedLookups(prev => ({
          ...prev,
          [cleanedBarcode || barcode]: {
            data: {
              ...data,
              source: responseData.source,
              matchType: response.data.matchType,
              matchConfidence: response.data.matchConfidence
            },
            timestamp: Date.now()
          }
        }));

        return {
          ...data,
          source: responseData.source,
          matchType: response.data.data?.matchType || response.data.matchType,
          matchConfidence: response.data.data?.matchConfidence || response.data.matchConfidence
        };
      } else {
        // No responseData found - likely no equipment found
        // Don't show notification here as it might be a duplicate
        return null;
      }
    } catch (error) {
      // Use specific error helper for better messages
      const errorMessage = getSpecificErrorMessage(error, 'lookup equipment', {
        resourceType: 'Equipment',
        barcode: cleanedBarcode || barcode
      });

      // Handle specific error cases
      if (error.response) {
        switch (error.response.status) {
          case 404:
            // Equipment not found - this is handled above, don't show error
            break;
          case 429:
            const retryAfter = error.response.headers['retry-after'];
            enqueueSnackbar(`Too many scans. Please wait ${retryAfter || 'a moment'} before scanning again.`, { variant: 'error' });
            break;
          case 401:
            enqueueSnackbar(errorMessage, { variant: 'error' });
            break;
          case 403:
            enqueueSnackbar(errorMessage, { variant: 'error' });
            break;
          default:
            if (error.response.status >= 500) {
              enqueueSnackbar(errorMessage, { variant: 'error' });
            }
        }
      } else if (error.request) {
        // Network error
        enqueueSnackbar(errorMessage, { variant: 'error' });
      }

      // Don't set error state for "not found" cases
      if (!errorMessage.includes('No equipment found') && !errorMessage.includes('was not found')) {
        setError(errorMessage);
      }
      
      // Log error for debugging
      console.error('Barcode lookup error:', {
        barcode: cleanedBarcode || barcode,
        error: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      // Always return null for failed lookups to trigger fallback
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [cachedLookups, enqueueSnackbar]);

  const handleScan = useCallback(async (barcode) => {
    if (!barcode || isLoading) return;
    
    // Ensure barcode is a string
    if (typeof barcode !== 'string') {
      console.warn('Invalid barcode type received:', typeof barcode);
      return;
    }

    setIsScanning(false);
    setScanResult(null);

    const result = await lookupBarcode(barcode);
    
    if (result) {
      setScanResult(result);
      
      // Prepare data for form pre-population
      const formData = {
        barcode: barcode,
        id: result.id || result.equipmentId,
        description: result.description,
        manufacturer: result.manufacturer || '',
        model: result.model || '',
        category: result.category || '',
        categoryId: result.categoryId,
        status: result.status || result.condition,
        source: result.source,
        equipmentId: result.equipmentId || result.equipment_id,
        currentLocation: result.currentLocation || result.current_location,
        warehouseId: result.warehouseId || result.warehouse_id,
        zoneId: result.zoneId || result.zone_id,
        serialNumber: result.serialNumber || result.serial_number
      };

      // Call the parent component's callback
      if (onScanComplete) {
        onScanComplete(formData, result.source === 'equipment_item');
      }

      // Auto-scroll to form fields after successful scan
      setTimeout(() => {
        const formElement = document.getElementById('receive-form-fields') || 
                           document.querySelector('form, [role="form"], .form-container, .equipment-form');
        if (formElement) {
          formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);

      // Only show "Equipment found" message for actual equipment items, not templates
      if (result.source === 'equipment_item') {
        const itemName = [result.manufacturer, result.model].filter(p => p && p.trim()).join(' ') || 'Equipment';
        enqueueSnackbar(`Equipment found: ${itemName}`, { variant: 'success' });
      }
      // Templates will be handled by the ReceiveInventory component
    } else {
      // Still call parent with barcode for new item creation
      const fallbackFormData = {
        barcode: barcode,
        id: '',
        description: '',
        manufacturer: '',
        model: '',
        category: '',
        status: '',
        source: 'manual',
        equipmentId: '',
        currentLocation: '',
        warehouseId: '',
        zoneId: ''
      };
      
      if (onScanComplete) {
        onScanComplete(fallbackFormData, false);
      }

      // Auto-scroll to form fields after fallback scan
      setTimeout(() => {
        const formElement = document.getElementById('receive-form-fields') || 
                           document.querySelector('form, [role="form"], .form-container, .equipment-form');
        if (formElement) {
          formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
      
      // Don't show notification here - it's already shown in lookupBarcode when !responseData.found
    }
  }, [isLoading, lookupBarcode, onScanComplete, enqueueSnackbar]);

  const startScanning = useCallback(() => {
    setIsScanning(true);
    setError(null);
    setScanResult(null);
  }, []);

  const stopScanning = useCallback(() => {
    setIsScanning(false);
  }, []);

  const clearCache = useCallback(() => {
    setCachedLookups({});
  }, []);

  const resetScanner = useCallback(() => {
    setIsScanning(false);
    setScanResult(null);
    setError(null);
  }, []);

  return {
    isScanning,
    scanResult,
    isLoading,
    error,
    startScanning,
    stopScanning,
    handleScan,
    clearCache,
    resetScanner,
    cachedLookups: Object.keys(cachedLookups).length
  };
};

export default useBarcodeScanner;